// Deterministic, read-only game session reconstruction.
// This module must run in both the browser and the server-side export renderer.
// There is no React, LiveKit, Date.now(), random(), or network dependency.

export const REPLAY_VERSION = 1;
export const REPLAY_CHECKPOINT_EVENT_INTERVAL = 150;
const MAX_EVENTS = 200_000;
const MAX_TIME_MS = 2_147_483_647;

export class ReplayFormatError extends Error {
  constructor(code) {
    super(code);
    this.name = 'ReplayFormatError';
    this.code = code;
  }
}

const object = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const safeObject = (value) => object(value) ? value : {};
const array = (value) => Array.isArray(value) ? value : [];
const integer = (value) => Number.isSafeInteger(value);
const parse = (value, context) => {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { throw new ReplayFormatError('INVALID_' + context); }
};

const copy = (value) => JSON.parse(JSON.stringify(value));

export const normalizeReplayBundle = ({ recording, initialSnapshot, assetsSnapshot, events = [] }) => {
  const snapshot = parse(initialSnapshot, 'INITIAL_SNAPSHOT');
  const assets = parse(assetsSnapshot, 'ASSETS_SNAPSHOT');
  if (!object(snapshot) || snapshot.version !== REPLAY_VERSION
    || !object(snapshot.session) || !object(snapshot.game)
    || !Array.isArray(snapshot.players) || !Array.isArray(snapshot.moves)
    || !object(assets) || assets.version !== REPLAY_VERSION) {
    throw new ReplayFormatError('UNSUPPORTED_REPLAY_VERSION');
  }
  if (!Array.isArray(events) || events.length > MAX_EVENTS) {
    throw new ReplayFormatError('INVALID_REPLAY_EVENTS');
  }
  let previousSequence = 0;
  let previousTime = -1;
  const normalized = events.map((event) => {
    if (!integer(event?.sequence) || event.sequence <= previousSequence
      || !integer(event?.atMs) || event.atMs < previousTime
      || event.atMs < 0 || event.atMs > MAX_TIME_MS
      || typeof event.kind !== 'string') {
      throw new ReplayFormatError('NON_MONOTONIC_EVENT_LOG');
    }
    previousSequence = event.sequence;
    previousTime = event.atMs;
    const payload = parse(event.payload, 'EVENT_PAYLOAD');
    if (!object(payload)) throw new ReplayFormatError('INVALID_EVENT_PAYLOAD');
    return { sequence: event.sequence, atMs: event.atMs, kind: event.kind, payload };
  });
  const elapsed = recording?.recordingStartedAt && recording?.recordingStoppedAt
    ? Math.max(0, new Date(recording.recordingStoppedAt).getTime() -
      new Date(recording.recordingStartedAt).getTime() -
      (recording.timelineOffsetMs || 0)) : null;
  const durationMs = Math.max(
    normalized.length ? normalized[normalized.length - 1].atMs : 0,
    integer(elapsed) && elapsed >= 0 && elapsed < MAX_TIME_MS ? elapsed : 0,
  );
  return { recording: recording || {}, snapshot, assets,
    events: normalized, durationMs };
};

// The public replay surface mirrors only cards the CONDUCTOR opened.
// Player card events remain in the journal for auditing but must never be
// presented to other viewers as a shared on-screen card.
// The side panel is a human-readable MOVEMENT log, not the complete
// technical journal. Voice/VAD, consent, recorder lifecycle, session updates
// and card telemetry stay in the immutable source for replay but are never
// rendered as distracting rows. The timeline is ordered start -> finish.
export const selectReplayMoveHistory = (events) =>
  (Array.isArray(events) ? events : [])
    .filter((event) => event.kind === 'move_applied' || event.kind === 'move_undone')
    .sort((a, b) => a.atMs - b.atMs || a.sequence - b.sequence);

export const getVisibleConductorCard = (replay) =>
  replay?.openedCards?.conductor || null;

// In a live Leela game players await the entry roll at cell 68. Their
// persisted currentCell can still be 1 while status is beforeEnter and no
// entry/start cell is assigned. Keep the semantic state unchanged, but project
// the correct visual position for both the browser replay and MP4 renderer.
export const resolveReplayDisplayCell = (player) => {
  if (player?.status === 'beforeEnter'
    && !Number.isInteger(player?.startCellNumber)) return 68;
  const cell = Number(player?.currentCell);
  return Number.isFinite(cell) && cell >= 1 && cell <= 72
    ? Math.round(cell) : 1;
};

export const initialReplayState = (bundle) => {
  const { snapshot, assets } = bundle;
  const players = Object.fromEntries(array(snapshot.players)
    .filter((player) => integer(player?.id))
    .map((player) => [player.id, {
      id: player.id, name: player.name || '', chipKey: player.chipKey || null,
      currentCell: player.currentCell || 1,
      startCellNumber: player.startCellNumber || null,
      status: player.status || 'inGame',
      isPaused: Boolean(player.isPaused), turnOrder: player.turnOrder || null,
    }]));
  const cardMap = Object.fromEntries(array(assets.deckSnapshot?.cards)
    .filter((card) => integer(card?.cellNumber))
    .map((card) => [card.cellNumber, copy(card)]));
  return {
    atMs: 0,
    sequence: 0,
    game: copy(snapshot.game),
    session: copy(snapshot.session),
    players,
    moves: array(snapshot.moves).map((move) => copy(move)),
    cardMap,
    openedCards: {}, // keyed by actor; multiple participants may open different cards
    dice: snapshot.session.lastDiceRoll || null,
    speakers: [],
    consent: {},
    status: 'recording',
    lastEvent: null,
    eventCount: 0,
  };
};

const validPlayerId = (id) => integer(id) && id > 0;
const withPlayer = (players, id, callback) => {
  if (!validPlayerId(id)) return players;
  const existing = players[id] || { id, currentCell: 1, name: '' };
  return { ...players, [id]: callback(existing) };
};
const sortedMoves = (moves) => [...moves].sort((a, b) =>
  (a.moveIndex || 0) - (b.moveIndex || 0) || (a.id || 0) - (b.id || 0));

export const reduceReplayEvent = (previous, event) => {
  if (!object(previous) || !object(event) || !object(event.payload)) {
    throw new ReplayFormatError('INVALID_REPLAY_REDUCTION');
  }
  const p = event.payload;
  const state = {
    ...previous,
    atMs: event.atMs,
    sequence: event.sequence,
    lastEvent: { kind: event.kind, payload: p },
    eventCount: previous.eventCount + 1,
  };
  switch (event.kind) {
    case 'recording_started':
      state.status = 'recording';
      return state;
    case 'recording_stopped':
      state.status = 'completed';
      state.speakers = [];
      return state;
    case 'recording_resumed':
      state.status = 'recording';
      state.speakers = [];
      return state;
    case 'dice_rolled':
      state.dice = copy(p);
      return state;
    case 'move_applied': {
      const move = {
        id: p.moveId, playerId: p.playerId, gameId: p.gameId,
        moveIndex: p.moveIndex, diceValue: p.diceValue,
        fromCell: p.fromCell, toCell: p.toCell,
        seriesId: p.seriesId || null, diceSeries: p.diceSeries || null,
        transitions: p.transitions || null, comment: p.comment || null,
        cardId: p.cardId || null,
      };
      state.moves = sortedMoves([
        ...previous.moves.filter((candidate) => candidate.id !== move.id),
        move,
      ]);
      if (validPlayerId(p.playerId) && integer(p.toCell)) {
        state.players = withPlayer(previous.players, p.playerId,
          (player) => ({
            ...player,
            currentCell: p.toCell,
            // The first recorded move corresponds to entering the game.
            // Do not leave the player's visual marker at 68 afterwards.
            ...(player.status === 'beforeEnter'
              ? { status: 'inGame', startCellNumber: p.toCell } : {}),
          }));
      }
      return state;
    }
    case 'move_undone': {
      const undoneIds = new Set(array(p.moveIds));
      state.moves = previous.moves.filter((move) => !undoneIds.has(move.id));
      if (integer(p.rollbackTo) && validPlayerId(p.playerId)) {
        state.players = withPlayer(previous.players, p.playerId,
          (player) => ({ ...player, currentCell: p.rollbackTo }));
      }
      if (validPlayerId(p.restoredCurrentPlayerId)) {
        state.session = { ...previous.session,
          currentPlayerId: p.restoredCurrentPlayerId };
      }
      return state;
    }
    case 'session_state': {
      state.session = {
        ...previous.session,
        currentPlayerId: p.currentPlayerId ?? previous.session.currentPlayerId,
        status: p.status || previous.session.status,
      };
      if (Array.isArray(p.players)) {
        const updated = { ...previous.players };
        for (const current of p.players) {
          if (!validPlayerId(current?.id)) continue;
          const player = updated[current.id] || { id: current.id, name: '' };
          updated[current.id] = {
            ...player,
            currentCell: integer(current.currentCell) ? current.currentCell : player.currentCell,
            status: current.status || player.status,
            // Undo/re-entry can restore the waiting-for-six state.
            // Clear any stale entry cell when the authoritative session says so.
            startCellNumber: current.status === 'beforeEnter'
              ? null : player.startCellNumber,
            isPaused: Boolean(current.isPaused),
          };
        }
        state.players = updated;
      }
      return state;
    }
    case 'turn_changed':
      state.session = { ...previous.session,
        currentPlayerId: p.currentPlayerId ?? null };
      return state;
    case 'player_paused':
    case 'player_resumed':
      state.players = withPlayer(previous.players, p.playerId, (player) => ({
        ...player, isPaused: event.kind === 'player_paused',
      }));
      return state;
    case 'session_status_changed':
      state.session = { ...previous.session,
        status: p.status || previous.session.status };
      return state;
    case 'card_opened': {
      if (!integer(p.cellNumber)) return state;
      const actor = typeof p.actor === 'string' && p.actor ? p.actor : 'unknown';
      state.openedCards = {
        ...previous.openedCards,
        [actor]: { cellNumber: p.cellNumber,
          card: previous.cardMap[p.cellNumber] || null,
          openedAtMs: event.atMs,
        },
      };
      return state;
    }
    case 'card_closed': {
      const actor = typeof p.actor === 'string' && p.actor ? p.actor : 'unknown';
      state.openedCards = { ...previous.openedCards };
      delete state.openedCards[actor];
      return state;
    }
    case 'speaker_activity':
      state.speakers = array(p.identities)
        .filter((id) => typeof id === 'string');
      return state;
    case 'voice_consent_requested':
      if (typeof p.actorIdentity === 'string') {
        state.consent = { ...previous.consent, [p.actorIdentity]: false };
        state.speakers = previous.speakers.filter((id) => id !== p.actorIdentity);
      }
      return state;
    case 'voice_consent_changed':
      if (typeof p.actorIdentity === 'string') {
        state.consent = { ...previous.consent,
          [p.actorIdentity]: Boolean(p.allowed) };
        if (!p.allowed) state.speakers = previous.speakers
          .filter((identity) => identity !== p.actorIdentity);
      }
      return state;
    default:
      // Unknown future event kinds must not break older replay versions.
      return state;
  }
};

export const buildReplayIndex = (bundle, checkpointInterval = REPLAY_CHECKPOINT_EVENT_INTERVAL) => {
  if (!integer(checkpointInterval) || checkpointInterval < 1 || checkpointInterval > 100_000) {
    throw new ReplayFormatError('INVALID_CHECKPOINT_INTERVAL');
  }
  let state = initialReplayState(bundle);
  const checkpoints = [{ eventIndex: -1, atMs: 0, state }];
  bundle.events.forEach((event, index) => {
    state = reduceReplayEvent(state, event);
    if ((index + 1) % checkpointInterval === 0) {
      checkpoints.push({ eventIndex: index, atMs: event.atMs, state });
    }
  });
  return { bundle, checkpoints, interval: checkpointInterval };
};

const lastIndexAtOrBefore = (items, atMs, getTime) => {
  let low = 0;
  let high = items.length - 1;
  let answer = -1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (getTime(items[mid]) <= atMs) {
      answer = mid;
      low = mid + 1;
    } else high = mid - 1;
  }
  return answer;
};

export const replayAt = (index, requestedMs) => {
  if (!Number.isFinite(requestedMs)) throw new ReplayFormatError('INVALID_SEEK_TIME');
  const { bundle, checkpoints } = index;
  const atMs = Math.max(0, Math.min(bundle.durationMs, Math.floor(requestedMs)));
  const finalIndex = lastIndexAtOrBefore(bundle.events, atMs, (event) => event.atMs);
  if (finalIndex < 0) return { ...initialReplayState(bundle), atMs };
  const checkpointIndex = lastIndexAtOrBefore(checkpoints, finalIndex, (entry) => entry.eventIndex);
  const checkpoint = checkpoints[Math.max(0, checkpointIndex)];
  let state = checkpoint.state;
  for (let i = checkpoint.eventIndex + 1; i <= finalIndex; i++) {
    state = reduceReplayEvent(state, bundle.events[i]);
  }
  return { ...state, atMs };
};

export const buildAudioTimeline = (segments = []) => {
  if (!Array.isArray(segments)) throw new ReplayFormatError('INVALID_AUDIO_MANIFEST');
  return segments.filter((segment) => integer(segment?.startMs)
    && integer(segment?.durationMs) && segment.durationMs > 0)
    .map((segment) => ({
      segmentIndex: segment.segmentIndex,
      startMs: segment.startMs,
      endMs: segment.startMs + segment.durationMs,
      durationMs: segment.durationMs,
      mimeType: segment.mimeType || 'audio/webm',
      byteSize: segment.byteSize || null,
    }))
    .sort((a,b) => a.startMs - b.startMs || a.segmentIndex - b.segmentIndex);
};

export const audioAt = (timeline, requestedMs) => {
  const time = Math.max(0, requestedMs);
  // Audio segments can overlap at boundaries. Prefer the later-starting segment.
  const index = lastIndexAtOrBefore(timeline, time, (segment) => segment.startMs);
  if (index < 0) return null;
  // A short later fragment may overlap a longer previous fragment.
  // Prefer the most recent still-active segment, not an already-ended one.
  for (let i = index; i >= 0; i--) {
    const segment = timeline[i];
    if (time < segment.endMs) {
      return {
        ...segment,
        offsetMs: time - segment.startMs,
        nextGapMs: null,
      };
    }
  }
  return null;
};

export const nextAudioBoundary = (timeline, requestedMs) => {
  const current = audioAt(timeline, requestedMs);
  if (current) return current.endMs;
  const future = timeline.find((segment) => segment.startMs > requestedMs);
  return future?.startMs ?? null;
};

export const splitEventPages = (pages) => {
  if (!Array.isArray(pages)) throw new ReplayFormatError('INVALID_EVENT_PAGES');
  const events = [];
  let previous = 0;
  for (const page of pages) {
    if (!Array.isArray(page?.events)) throw new ReplayFormatError('INVALID_EVENT_PAGE');
    for (const event of page.events) {
      if (!integer(event.sequence) || event.sequence <= previous) {
        throw new ReplayFormatError('OUT_OF_ORDER_EVENT_PAGE');
      }
      events.push(event);
      previous = event.sequence;
    }
  }
  return events;
};
